import type {
  EquipmentAuditEntryScanApiData,
  EquipmentAuditEntryScanApiResponses,
  EquipmentAuditSessionCancelApiData,
  EquipmentAuditSessionCancelApiResponses,
  EquipmentAuditSessionCompleteApiData,
  EquipmentAuditSessionCompleteApiResponses,
  EquipmentAuditSessionCreateApiData,
  EquipmentAuditSessionCreateApiResponses,
  EquipmentAuditSessionGetApiData,
  EquipmentAuditSessionGetApiResponses,
  EquipmentAuditSessionListApiData,
  EquipmentAuditSessionListApiResponses,
  EquipmentCreateApiData,
  EquipmentCreateApiResponses,
  EquipmentDeleteApiData,
  EquipmentDeleteApiResponses,
  EquipmentGetApiData,
  EquipmentGetApiResponses,
  EquipmentItemCheckInApiData,
  EquipmentItemCheckInApiResponses,
  EquipmentItemCheckOutApiData,
  EquipmentItemCheckOutApiResponses,
  EquipmentItemCreateApiData,
  EquipmentItemCreateApiResponses,
  EquipmentItemDeleteApiData,
  EquipmentItemDeleteApiResponses,
  EquipmentItemFoundApiData,
  EquipmentItemFoundApiResponses,
  EquipmentItemGetApiData,
  EquipmentItemGetApiResponses,
  EquipmentItemListApiData,
  EquipmentItemListApiResponses,
  EquipmentItemLostApiData,
  EquipmentItemLostApiResponses,
  EquipmentItemMaintenanceApiData,
  EquipmentItemMaintenanceApiResponses,
  EquipmentItemMovementListApiData,
  EquipmentItemMovementListApiResponses,
  EquipmentItemPatchApiData,
  EquipmentItemPatchApiResponses,
  EquipmentItemScanApiData,
  EquipmentItemScanApiResponses,
  EquipmentKitAssignApiData,
  EquipmentKitAssignApiResponses,
  EquipmentKitRequirementListApiData,
  EquipmentKitRequirementListApiResponses,
  EquipmentKitRequirementSetApiData,
  EquipmentKitRequirementSetApiResponses,
  EquipmentListApiData,
  EquipmentListApiResponses,
  EquipmentPatchApiData,
  EquipmentPatchApiResponses,
  EquipmentStockAlertBreachedApiData,
  EquipmentStockAlertBreachedApiResponses,
  EquipmentStockAlertCreateApiData,
  EquipmentStockAlertCreateApiResponses,
  EquipmentStockAlertDeleteApiData,
  EquipmentStockAlertDeleteApiResponses,
  EquipmentStockAlertGetApiData,
  EquipmentStockAlertGetApiResponses,
  EquipmentStockAlertListApiData,
  EquipmentStockAlertListApiResponses,
  EquipmentStockAlertPatchApiData,
  EquipmentStockAlertPatchApiResponses,
  EquipmentTemplateCreateApiData,
  EquipmentTemplateCreateApiResponses,
  EquipmentTemplateDeleteApiData,
  EquipmentTemplateDeleteApiResponses,
  EquipmentTemplateGetApiData,
  EquipmentTemplateGetApiResponses,
  EquipmentTemplateListApiData,
  EquipmentTemplateListApiResponses,
  EquipmentTemplatePatchApiData,
  EquipmentTemplatePatchApiResponses,
  EquipmentTransferCancelApiData,
  EquipmentTransferCancelApiResponses,
  EquipmentTransferCompleteApiData,
  EquipmentTransferCompleteApiResponses,
  EquipmentTransferCreateApiData,
  EquipmentTransferCreateApiResponses,
  EquipmentTransferGetApiData,
  EquipmentTransferGetApiResponses,
  EquipmentTransferListApiData,
  EquipmentTransferListApiResponses,
  EquipmentTransferRejectApiData,
  EquipmentTransferRejectApiResponses,
  ServiceWorkerPpeSizeGetApiData,
  ServiceWorkerPpeSizeGetApiResponses,
  ServiceWorkerPpeSizePatchApiData,
  ServiceWorkerPpeSizePatchApiResponses
} from '@navio/nsdk';

import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';
import { equipmentImageUrls } from './equipmentImageUrls';
import { equipmentPurchasePlanUrls } from './equipmentPurchasePlanUrls';
import { equipmentVendorUrls } from './equipmentVendorUrls';

export const getEquipmentUrls = {
  ...equipmentImageUrls,
  ...equipmentPurchasePlanUrls,
  ...equipmentVendorUrls,
  getPaginatedEquipmentItems: dataEndpoint<EquipmentItemListApiData, EquipmentItemListApiResponses>({
    getUrl: (queryParams) => apiUrl('inventory/equipment-items/', queryParams)
  }),
  getEquipmentItem: dataEndpoint<EquipmentItemGetApiData, EquipmentItemGetApiResponses>({
    getUrl: ({ equipmentItemId }) => apiUrl(`inventory/equipment-items/${equipmentItemId}/`)
  }),
  postEquipmentItem: responseEndpoint<EquipmentItemCreateApiData, EquipmentItemCreateApiResponses>({
    getUrl: () => apiUrl('inventory/equipment-items/'),
    getBody: (data) => data
  }),
  patchEquipmentItem: responseEndpoint<EquipmentItemPatchApiData, EquipmentItemPatchApiResponses>({
    getUrl: ({ equipmentItemId }) => apiUrl(`inventory/equipment-items/${equipmentItemId}/`),
    getBody: ({ equipmentItemId: _, ...body }) => body
  }),
  deleteEquipmentItem: dataEndpoint<EquipmentItemDeleteApiData, EquipmentItemDeleteApiResponses>({
    getUrl: ({ equipmentItemId }) => apiUrl(`inventory/equipment-items/${equipmentItemId}/`)
  }),
  postEquipmentItemCheckOut: responseEndpoint<EquipmentItemCheckOutApiData, EquipmentItemCheckOutApiResponses>({
    getUrl: ({ equipmentItemId }) => apiUrl(`inventory/equipment-items/${equipmentItemId}/check-out/`),
    getBody: ({ equipmentItemId: _, ...body }) => body
  }),
  postEquipmentItemCheckIn: responseEndpoint<EquipmentItemCheckInApiData, EquipmentItemCheckInApiResponses>({
    getUrl: ({ equipmentItemId }) => apiUrl(`inventory/equipment-items/${equipmentItemId}/check-in/`),
    getBody: ({ equipmentItemId: _, ...body }) => body
  }),
  postEquipmentItemMaintenance: responseEndpoint<EquipmentItemMaintenanceApiData, EquipmentItemMaintenanceApiResponses>(
    {
      getUrl: ({ equipmentItemId }) => apiUrl(`inventory/equipment-items/${equipmentItemId}/maintenance/`),
      getBody: ({ equipmentItemId: _, ...body }) => body
    }
  ),
  postEquipmentItemLost: responseEndpoint<EquipmentItemLostApiData, EquipmentItemLostApiResponses>({
    getUrl: ({ equipmentItemId }) => apiUrl(`inventory/equipment-items/${equipmentItemId}/lost/`),
    getBody: ({ equipmentItemId: _, ...body }) => body
  }),
  postEquipmentItemFound: responseEndpoint<EquipmentItemFoundApiData, EquipmentItemFoundApiResponses>({
    getUrl: ({ equipmentItemId }) => apiUrl(`inventory/equipment-items/${equipmentItemId}/found/`),
    getBody: ({ equipmentItemId: _, ...body }) => body
  }),
  getPaginatedEquipmentItemMovements: dataEndpoint<
    EquipmentItemMovementListApiData,
    EquipmentItemMovementListApiResponses
  >({
    getUrl: ({ equipmentItemId, ...queryParams }) =>
      apiUrl(`inventory/equipment-items/${equipmentItemId}/movements/`, queryParams)
  }),
  getEquipmentItemByScan: dataEndpoint<EquipmentItemScanApiData, EquipmentItemScanApiResponses>({
    getUrl: (queryParams) => apiUrl('inventory/scan/', queryParams)
  }),

  getPaginatedEquipmentKits: dataEndpoint<EquipmentListApiData, EquipmentListApiResponses>({
    getUrl: (queryParams) => apiUrl('inventory/equipment/', queryParams)
  }),
  getEquipmentKit: dataEndpoint<EquipmentGetApiData, EquipmentGetApiResponses>({
    getUrl: ({ equipmentId }) => apiUrl(`inventory/equipment/${equipmentId}/`)
  }),
  postEquipmentKit: responseEndpoint<EquipmentCreateApiData, EquipmentCreateApiResponses>({
    getUrl: () => apiUrl('inventory/equipment/'),
    getBody: (data) => data
  }),
  patchEquipmentKit: responseEndpoint<EquipmentPatchApiData, EquipmentPatchApiResponses>({
    getUrl: ({ equipmentId }) => apiUrl(`inventory/equipment/${equipmentId}/`),
    getBody: ({ equipmentId: _, ...body }) => body
  }),
  deleteEquipmentKit: dataEndpoint<EquipmentDeleteApiData, EquipmentDeleteApiResponses>({
    getUrl: ({ equipmentId }) => apiUrl(`inventory/equipment/${equipmentId}/`)
  }),
  getPaginatedEquipmentKitRequirements: dataEndpoint<
    EquipmentKitRequirementListApiData,
    EquipmentKitRequirementListApiResponses
  >({
    getUrl: ({ equipmentId, ...queryParams }) => apiUrl(`inventory/equipment/${equipmentId}/requirements/`, queryParams)
  }),
  postEquipmentKitRequirements: responseEndpoint<
    EquipmentKitRequirementSetApiData,
    EquipmentKitRequirementSetApiResponses
  >({
    getUrl: ({ equipmentId }) => apiUrl(`inventory/equipment/${equipmentId}/requirements/`),
    getBody: ({ equipmentId: _, ...body }) => body
  }),
  postEquipmentKitAssign: responseEndpoint<EquipmentKitAssignApiData, EquipmentKitAssignApiResponses>({
    getUrl: ({ equipmentId }) => apiUrl(`inventory/equipment/${equipmentId}/assign/`),
    getBody: ({ equipmentId: _, ...body }) => body
  }),

  getPaginatedEquipmentTemplates: dataEndpoint<EquipmentTemplateListApiData, EquipmentTemplateListApiResponses>({
    getUrl: (queryParams) => apiUrl('inventory/equipment-templates/', queryParams)
  }),
  getEquipmentTemplate: dataEndpoint<EquipmentTemplateGetApiData, EquipmentTemplateGetApiResponses>({
    getUrl: ({ equipmentTemplateId }) => apiUrl(`inventory/equipment-templates/${equipmentTemplateId}/`)
  }),
  postEquipmentTemplate: responseEndpoint<EquipmentTemplateCreateApiData, EquipmentTemplateCreateApiResponses>({
    getUrl: () => apiUrl('inventory/equipment-templates/'),
    getBody: (data) => data
  }),
  patchEquipmentTemplate: responseEndpoint<EquipmentTemplatePatchApiData, EquipmentTemplatePatchApiResponses>({
    getUrl: ({ equipmentTemplateId }) => apiUrl(`inventory/equipment-templates/${equipmentTemplateId}/`),
    getBody: ({ equipmentTemplateId: _, ...body }) => body
  }),
  deleteEquipmentTemplate: dataEndpoint<EquipmentTemplateDeleteApiData, EquipmentTemplateDeleteApiResponses>({
    getUrl: ({ equipmentTemplateId }) => apiUrl(`inventory/equipment-templates/${equipmentTemplateId}/`)
  }),

  getPaginatedEquipmentTransfers: dataEndpoint<EquipmentTransferListApiData, EquipmentTransferListApiResponses>({
    getUrl: (queryParams) => apiUrl('inventory/equipment-transfers/', queryParams)
  }),
  getEquipmentTransfer: dataEndpoint<EquipmentTransferGetApiData, EquipmentTransferGetApiResponses>({
    getUrl: ({ equipmentTransferId }) => apiUrl(`inventory/equipment-transfers/${equipmentTransferId}/`)
  }),
  postEquipmentTransfer: responseEndpoint<EquipmentTransferCreateApiData, EquipmentTransferCreateApiResponses>({
    getUrl: () => apiUrl('inventory/equipment-transfers/'),
    getBody: (data) => data
  }),
  postEquipmentTransferComplete: responseEndpoint<
    EquipmentTransferCompleteApiData,
    EquipmentTransferCompleteApiResponses
  >({
    getUrl: ({ equipmentTransferId }) => apiUrl(`inventory/equipment-transfers/${equipmentTransferId}/complete/`),
    getBody: ({ equipmentTransferId: _, ...body }) => body
  }),
  postEquipmentTransferReject: responseEndpoint<EquipmentTransferRejectApiData, EquipmentTransferRejectApiResponses>({
    getUrl: ({ equipmentTransferId }) => apiUrl(`inventory/equipment-transfers/${equipmentTransferId}/reject/`),
    getBody: ({ equipmentTransferId: _, ...body }) => body
  }),
  postEquipmentTransferCancel: responseEndpoint<EquipmentTransferCancelApiData, EquipmentTransferCancelApiResponses>({
    getUrl: ({ equipmentTransferId }) => apiUrl(`inventory/equipment-transfers/${equipmentTransferId}/cancel/`),
    getBody: ({ equipmentTransferId: _, ...body }) => body
  }),

  getPaginatedEquipmentAudits: dataEndpoint<EquipmentAuditSessionListApiData, EquipmentAuditSessionListApiResponses>({
    getUrl: (queryParams) => apiUrl('inventory/equipment-audits/', queryParams)
  }),
  getEquipmentAudit: dataEndpoint<EquipmentAuditSessionGetApiData, EquipmentAuditSessionGetApiResponses>({
    getUrl: ({ equipmentAuditSessionId }) => apiUrl(`inventory/equipment-audits/${equipmentAuditSessionId}/`)
  }),
  postEquipmentAudit: responseEndpoint<EquipmentAuditSessionCreateApiData, EquipmentAuditSessionCreateApiResponses>({
    getUrl: () => apiUrl('inventory/equipment-audits/'),
    getBody: (data) => data
  }),
  postEquipmentAuditScan: responseEndpoint<EquipmentAuditEntryScanApiData, EquipmentAuditEntryScanApiResponses>({
    getUrl: ({ equipmentAuditSessionId }) => apiUrl(`inventory/equipment-audits/${equipmentAuditSessionId}/scans/`),
    getBody: ({ equipmentAuditSessionId: _, ...body }) => body
  }),
  postEquipmentAuditComplete: responseEndpoint<
    EquipmentAuditSessionCompleteApiData,
    EquipmentAuditSessionCompleteApiResponses
  >({
    getUrl: ({ equipmentAuditSessionId }) => apiUrl(`inventory/equipment-audits/${equipmentAuditSessionId}/complete/`),
    getBody: ({ equipmentAuditSessionId: _, ...body }) => body
  }),
  postEquipmentAuditCancel: responseEndpoint<
    EquipmentAuditSessionCancelApiData,
    EquipmentAuditSessionCancelApiResponses
  >({
    getUrl: ({ equipmentAuditSessionId }) => apiUrl(`inventory/equipment-audits/${equipmentAuditSessionId}/cancel/`),
    getBody: ({ equipmentAuditSessionId: _, ...body }) => body
  }),

  getPaginatedEquipmentStockAlerts: dataEndpoint<EquipmentStockAlertListApiData, EquipmentStockAlertListApiResponses>({
    getUrl: (queryParams) => apiUrl('inventory/equipment-stock-alerts/', queryParams)
  }),
  getBreachedEquipmentStockAlerts: dataEndpoint<
    EquipmentStockAlertBreachedApiData,
    EquipmentStockAlertBreachedApiResponses
  >({
    getUrl: (queryParams) => apiUrl('inventory/equipment-stock-alerts/breached/', queryParams)
  }),
  getEquipmentStockAlert: dataEndpoint<EquipmentStockAlertGetApiData, EquipmentStockAlertGetApiResponses>({
    getUrl: ({ equipmentStockAlertId }) => apiUrl(`inventory/equipment-stock-alerts/${equipmentStockAlertId}/`)
  }),
  postEquipmentStockAlert: responseEndpoint<EquipmentStockAlertCreateApiData, EquipmentStockAlertCreateApiResponses>({
    getUrl: () => apiUrl('inventory/equipment-stock-alerts/'),
    getBody: (data) => data
  }),
  patchEquipmentStockAlert: responseEndpoint<EquipmentStockAlertPatchApiData, EquipmentStockAlertPatchApiResponses>({
    getUrl: ({ equipmentStockAlertId }) => apiUrl(`inventory/equipment-stock-alerts/${equipmentStockAlertId}/`),
    getBody: ({ equipmentStockAlertId: _, ...body }) => body
  }),
  deleteEquipmentStockAlert: dataEndpoint<EquipmentStockAlertDeleteApiData, EquipmentStockAlertDeleteApiResponses>({
    getUrl: ({ equipmentStockAlertId }) => apiUrl(`inventory/equipment-stock-alerts/${equipmentStockAlertId}/`)
  }),

  getServiceWorkerPpeSize: dataEndpoint<ServiceWorkerPpeSizeGetApiData, ServiceWorkerPpeSizeGetApiResponses>({
    getUrl: ({ serviceWorkerId }) => apiUrl(`inventory/service-workers/${serviceWorkerId}/ppe-sizes/`)
  }),
  patchServiceWorkerPpeSize: responseEndpoint<ServiceWorkerPpeSizePatchApiData, ServiceWorkerPpeSizePatchApiResponses>({
    getUrl: ({ serviceWorkerId }) => apiUrl(`inventory/service-workers/${serviceWorkerId}/ppe-sizes/`),
    getBody: ({ serviceWorkerId: _, ...body }) => body
  })
};
